import { AddressAutofill } from "@mapbox/search-js-react";
import { first, get, last } from "lodash";
import { useCallback, useState } from "react";
import { WrappedFieldProps } from "redux-form";

import { translate } from "src/services";
import {
  FormError,
  FormGroup,
  FormGroupClear,
  FormGroupClearContainer,
  FormInput,
  FormLabel,
} from "src/styled";
import { theme } from "src/styles";

interface Props extends WrappedFieldProps {
  disabled?: boolean;
  readOnly?: boolean;
  instantValidation?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  label?: string;
  margin?: string;
  placeholder?: string;
  setEmptyValue?: () => void;
  withPlaceholder?: boolean;
  fullWidth?: boolean;
}

const LocationPickerMapbox = ({
  input,
  meta,
  setEmptyValue = () => {},
  disabled,
  instantValidation,
  isClearable,
  isLoading,
  label,
  margin,
  meta: { dirty, asyncValidating, submitFailed, error },
  placeholder,
  withPlaceholder,
  readOnly,
  fullWidth,
  ...props
}: Props) => {
  const getFormattedAddress = useCallback(
    () => input.value.formattedAddress,
    [input.value.formattedAddress]
  );

  const getCity = (place: any) =>
    get(place, "address_level1") ||
    get(place, "address_level2") ||
    get(place, "place") ||
    get(place, "address_level3");
  const getState = (place: any) => get(place, "region");
  const getCountry = (place: any) => get(place, "country");
  const getZip = (place: any) => get(place, "postcode");
  const getStreetNumber = (place: any) => get(place, "address_number");
  const getStreet = (place: any) => get(place, "street");
  const getLat = (place: any) => last(place) || undefined;
  const getLng = (place: any) => first(place) || undefined;

  const getLocation = (place: any) => {
    const coordinatesData = get(place.features[0], "geometry.coordinates");
    const addressData = get(place.features[0], "properties");
    return {
      city: getCity(addressData),
      country: getCountry(addressData),
      formattedAddress: get(addressData, "full_address") || "",
      latitude: getLat(coordinatesData),
      longitude: getLng(coordinatesData),
      state: getState(addressData),
      street: getStreet(addressData),
      streetNumber: getStreetNumber(addressData),
      zip: getZip(addressData),
    };
  };

  const [value, setValue] = useState(getFormattedAddress());

  const onChange = (value: string) => {
    if (value === "") setEmptyValue();
    setValue(value);
  };

  const clearInput = useCallback(() => {
    setValue(null);
    setEmptyValue();
    input.onChange(null);
  }, [input, setEmptyValue]);

  const onLocationPick = (place: any) => {
    const locationDetails = getLocation(place);

    setValue(locationDetails.formattedAddress || "");
    locationDetails.formattedAddress
      ? input.onChange(locationDetails)
      : input.onChange(null);
  };

  return (
    <FormGroup
      hasValue={value}
      isLoading={isLoading || asyncValidating}
      margin={margin}
      width={fullWidth ? "100%" : undefined}
    >
      {!!label && !placeholder && <FormLabel>{label}</FormLabel>}
      <FormGroupClearContainer>
        <AddressAutofill
          accessToken={process.env.REACT_APP_MAPBOX_API_KEY || ""}
          onRetrieve={onLocationPick}
          onChange={onChange}
          browserAutofillEnabled={true}
          theme={{
            variables: {
              colorPrimary: theme.brandPrimary,
            },
          }}
          confirmOnBrowserAutofill={{
            minimap: true,
          }}
        >
          <FormInput
            disabled={disabled}
            placeholder={
              placeholder ||
              (withPlaceholder ? translate("common.address") : "")
            }
            autoComplete="address-line1"
            readOnly={readOnly}
            type="text"
            value={value}
            rightPadding={isClearable ? "32px" : undefined}
          />
        </AddressAutofill>

        {isClearable && !!value && (
          <FormGroupClear
            smallMargin
            disabled={disabled}
            onClick={clearInput}
          />
        )}
      </FormGroupClearContainer>

      {(submitFailed || (dirty && instantValidation)) && error && (
        <FormError>{error}</FormError>
      )}
    </FormGroup>
  );
};

export default LocationPickerMapbox;
